import React from 'react';

import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { capitalize } from 'lodash';

import { usePermission } from '@admin/hooks/use_permission';
import { AVAILABILITY_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { OrderSubtypeEnum, OrderTypeEnum } from '@admin/schema';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { MutedPanel } from '@admin/styled/muted_panel';
import { MutedText } from '@admin/styled/muted_text';
import { SectionActions } from '@admin/styled/section_actions';
import { Table } from '@admin/styled/table';
import { TableWrap } from '@admin/styled/table_wrap';

const DayCellDetail = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7385;
  line-height: 1.4;
`;

interface OperatingScheduleTemplate {
  id: string;
  effectiveDate: string;
  operatesSunday: boolean;
  operatesMonday: boolean;
  operatesTuesday: boolean;
  operatesWednesday: boolean;
  operatesThursday: boolean;
  operatesFriday: boolean;
  operatesSaturday: boolean;
  orderType?: OrderTypeEnum | null;
  orderSubtype?: OrderSubtypeEnum | null;

  sundayBookedPercentageCloseTrigger?: number | null;
  mondayBookedPercentageCloseTrigger?: number | null;
  tuesdayBookedPercentageCloseTrigger?: number | null;
  wednesdayBookedPercentageCloseTrigger?: number | null;
  thursdayBookedPercentageCloseTrigger?: number | null;
  fridayBookedPercentageCloseTrigger?: number | null;
  saturdayBookedPercentageCloseTrigger?: number | null;

  sundayBookedPercentageCloseTriggerMinDaysOut?: number | null;
  mondayBookedPercentageCloseTriggerMinDaysOut?: number | null;
  tuesdayBookedPercentageCloseTriggerMinDaysOut?: number | null;
  wednesdayBookedPercentageCloseTriggerMinDaysOut?: number | null;
  thursdayBookedPercentageCloseTriggerMinDaysOut?: number | null;
  fridayBookedPercentageCloseTriggerMinDaysOut?: number | null;
  saturdayBookedPercentageCloseTriggerMinDaysOut?: number | null;
}

function DayCell({
  operates,
  closeTrigger,
  minDaysOut,
}: {
  operates: boolean;
  closeTrigger?: number | null;
  minDaysOut?: number | null;
}) {
  return (
    <>
      {operates ? 'Open' : <MutedText>Closed</MutedText>}
      {operates && closeTrigger && <DayCellDetail>Closed when bookings reach {closeTrigger}% capacity</DayCellDetail>}
      {operates && closeTrigger && minDaysOut && (
        <DayCellDetail>Reopens when within {minDaysOut} days out, regardless of bookings</DayCellDetail>
      )}
    </>
  );
}

export const OperatingSchedule: React.FC<{
  operatingScheduleTemplates: OperatingScheduleTemplate[];
  manageHref: string;
}> = ({ operatingScheduleTemplates, manageHref }) => {
  const canManageSchedule = usePermission(AVAILABILITY_ADMIN.resource, AVAILABILITY_ADMIN.actions.update);

  function jobTypeDisplayName(orderType: OrderTypeEnum, orderSubtype: OrderSubtypeEnum) {
    if (orderType === OrderTypeEnum.Move) {
      return 'Move';
    }

    return `${capitalize(orderSubtype)} ${capitalize(orderType)}`;
  }

  const showEffectiveDate = !operatingScheduleTemplates.every(
    (template) => template.effectiveDate === operatingScheduleTemplates[0].effectiveDate,
  );

  const showJobType = !operatingScheduleTemplates.every((template) => !template.orderType && !template.orderSubtype);

  const scheduleTableContent = (
    <>
      <thead>
        <tr>
          {showEffectiveDate && <th>Effective Date</th>}
          {showJobType && <th>Job Type</th>}
          <th>Sunday</th>
          <th>Monday</th>
          <th>Tuesday</th>
          <th>Wednesday</th>
          <th>Thursday</th>
          <th>Friday</th>
          <th>Saturday</th>
        </tr>
      </thead>
      <tbody>
        {operatingScheduleTemplates.map((template) => (
          <tr key={template.id}>
            {showEffectiveDate && <td>{DateTime.fromISO(template.effectiveDate).toISODate()}</td>}
            {showJobType && (
              <td>
                {template.orderType &&
                  template.orderSubtype &&
                  jobTypeDisplayName(template.orderType, template.orderSubtype)}
              </td>
            )}

            <td>
              <DayCell
                operates={template.operatesSunday}
                closeTrigger={template.sundayBookedPercentageCloseTrigger}
                minDaysOut={template.sundayBookedPercentageCloseTriggerMinDaysOut}
              />
            </td>
            <td>
              <DayCell
                operates={template.operatesMonday}
                closeTrigger={template.mondayBookedPercentageCloseTrigger}
                minDaysOut={template.mondayBookedPercentageCloseTriggerMinDaysOut}
              />
            </td>
            <td>
              <DayCell
                operates={template.operatesTuesday}
                closeTrigger={template.tuesdayBookedPercentageCloseTrigger}
                minDaysOut={template.tuesdayBookedPercentageCloseTriggerMinDaysOut}
              />
            </td>
            <td>
              <DayCell
                operates={template.operatesWednesday}
                closeTrigger={template.wednesdayBookedPercentageCloseTrigger}
                minDaysOut={template.wednesdayBookedPercentageCloseTriggerMinDaysOut}
              />
            </td>
            <td>
              <DayCell
                operates={template.operatesThursday}
                closeTrigger={template.thursdayBookedPercentageCloseTrigger}
                minDaysOut={template.thursdayBookedPercentageCloseTriggerMinDaysOut}
              />
            </td>
            <td>
              <DayCell
                operates={template.operatesFriday}
                closeTrigger={template.fridayBookedPercentageCloseTrigger}
                minDaysOut={template.fridayBookedPercentageCloseTriggerMinDaysOut}
              />
            </td>
            <td>
              <DayCell
                operates={template.operatesSaturday}
                closeTrigger={template.saturdayBookedPercentageCloseTrigger}
                minDaysOut={template.saturdayBookedPercentageCloseTriggerMinDaysOut}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </>
  );

  return (
    <Card>
      {canManageSchedule && (
        <CardToolbar>
          <SectionActions style={{ width: '100%', justifyContent: 'flex-end' }}>
            <ButtonLink variant="secondary" size="sm" to={manageHref}>
              Manage operating schedule
            </ButtonLink>
          </SectionActions>
        </CardToolbar>
      )}
      {operatingScheduleTemplates.length === 0 ? (
        <MutedPanel>No operating schedule is configured yet.</MutedPanel>
      ) : (
        <TableWrap>
          <Table>{scheduleTableContent}</Table>
        </TableWrap>
      )}
    </Card>
  );
};
